import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { Client } from '@shared/models';
import { HeaderService } from '@shared/services';
import { PlainObject, QueryOptions } from 'odata-query';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { GstarClient } from '../../../web-api-client';
import { mapEntityFromResponseNested } from '../../admin/mappers/entity/map-entity-from-response-nested';
import { Collection } from '../../admin/models';
import { Entity } from '../../portfolio/models/entity';

@Injectable({
  providedIn: 'root',
})
export class ManageEntitiesService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  query: WritableSignal<Partial<QueryOptions<Entity>>> = signal({
    filter: '',
  });

  get(query?: Partial<QueryOptions<Entity>>): Observable<Collection<Entity>> {
    return this.gstarClient
      .entitiesGET2(
        query?.select?.toString(),
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapEntityFromResponseNested(response)
            ) ?? [],
        }))
      );
  }

  entitiesByClient: Observable<Collection<Entity>> = combineLatest([
    toObservable(this.headerService.selectedClient),
    toObservable(this.query),
  ]).pipe(
    switchMap(
      ([client, query]: [
        Client | undefined,
        Partial<QueryOptions<Entity>>,
      ]) => {
        const builtQuery = this.generateEntitiesFilter(client, query);
        return this.get({ ...builtQuery, count: true });
      }
    )
  );

  private generateEntitiesFilter(
    selectedClient: Client | undefined,
    query: Partial<QueryOptions<Entity>>
  ): Partial<QueryOptions<Entity>> {
    if (selectedClient) {
      if (!query.filter) {
        query.filter = {};
      }
      this.mergeQuery(query, this.setClientFilter(selectedClient?.id));
    } else if (query.filter) {
      query.filter = this.removeProp(query.filter as PlainObject, [
        'client',
        'clientId',
      ]);
    }

    if (
      query.filter &&
      Object.entries(query.filter as PlainObject).length === 0
    ) {
      delete query.filter;
    }

    return query;
  }

  updateFilter(queryFilter: PlainObject): void {
    const currentQuery = this.query();
    currentQuery.skip = 0;

    if (Object.entries(queryFilter).length === 0) {
      delete currentQuery.filter;
    } else {
      currentQuery.filter = queryFilter;
    }

    this.query.set(currentQuery);
  }

  private mergeQuery(
    query: Partial<QueryOptions<Entity>>,
    updatedFilter: Entity
  ): void {
    Object.assign(query.filter as JSONValue, updatedFilter);
  }

  private setClientFilter(clientId: number): Entity {
    return <Entity>{ client: { clientId } };
  }

  private removeProp(filters: PlainObject, propPath: string[]): PlainObject {
    let counter = 0;

    while (counter < propPath.length) {
      const currentProp = propPath[counter];
      const hasProp = Object.keys(filters).some(prop => prop === currentProp);

      if (hasProp) {
        delete filters[currentProp];
      } else {
        if (typeof filters[currentProp] === 'object') {
          this.removeProp(
            filters[currentProp],
            propPath.filter((_path: string, index: number) => index > counter)
          );
        }
      }
      counter++;
    }

    return filters;
  }
}
